import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { ApiAutenticacaoService } from '../../services/api-autenticacao.service';
import { ehRequestAdmin } from '../../features/admin/core/admin-auth.interceptor';

interface RetornoErroApi {
  idLog?: number;
  detalhe?: string;
  mensagem?: string;
  sucesso?: boolean;
  errorMessage?: string;
  mensagens?: string[];
}

const SKIP_ERROR_HEADER = 'X-Skip-Error';

export const authInterceptor: HttpInterceptorFn = (req, next) => {

  if (ehRequestAdmin(req.url)) {
    return next(req);
  }

  const apiAuth = inject(ApiAutenticacaoService);
  const router = inject(Router);

  const ehUrlBioAtlas = req.url.includes('medicalharbour.com');
  const ehUrlIpify = req.url.includes('api.ipify.org');
  const ehUrlAcessosOnline = req.url.includes('acessosonline.com.br');
  const ehUrlMicrosoft = req.url.includes('login.microsoftonline.com');

  const token = apiAuth.tokenLogado();
  const ehUrlExterna = ehUrlBioAtlas || ehUrlIpify || ehUrlAcessosOnline || ehUrlMicrosoft;

  let requisicao = req;

  const deveIgnorarErro = requisicao.headers.has(SKIP_ERROR_HEADER);
  if (deveIgnorarErro) {
    requisicao = requisicao.clone({ headers: requisicao.headers.delete(SKIP_ERROR_HEADER) });
  }

  const ehUrlInternaSemAuthHeader = !!token && !requisicao.headers.get('Authorization') && !ehUrlExterna;
  if (ehUrlInternaSemAuthHeader) {
    requisicao = requisicao.clone({
      headers: requisicao.headers.set('Authorization', 'Basic ' + btoa(`${environment.AutenticacaoApi_HeaderUser}:${token}`))
    });
  }

  const tempoLimiteMs = environment.production ? 15000 : 60000;

  return next(requisicao).pipe(
    timeout(tempoLimiteMs),
    catchError((erro: HttpErrorResponse) => {

      const localChamada = requisicao.headers.get('X-Unip-Local');

      if (deveIgnorarErro) {
        return throwError(() => erro);
      }

      if (erro.status === 401 || erro.status === 403) {
        return tratarSessaoInvalida(erro, requisicao, next, apiAuth, router, localChamada);
      }

      if (ehUrlIpify) {
        return ofMockedIpify();
      }

      if (ehUrlAcessosOnline) {
        return throwError(() => ({ message: 'Houve um erro com a comunicação com o servidor. Por favor, tente novamente.' }));
      }

      return tratarErroGenerico(erro);
    })
  );
};

function tratarSessaoInvalida(
  erro: HttpErrorResponse,
  requisicao: HttpRequest<unknown>,
  next: HttpHandlerFn,
  apiAuth: ApiAutenticacaoService,
  router: Router,
  localChamada: string | null
): Observable<HttpEvent<unknown>> {

  if (localChamada === 'validation-token') {
    return throwError(() => ({ message: 'Houve um erro com a comunicação com o servidor. Por favor, tente novamente.' }));
  }

  // Um 401/403 na própria chamada de refresh-token nunca deve tentar refresh de novo
  // (evitaria recursão infinita) - apenas encerra a sessão local e volta ao login.
  if (localChamada === 'refresh-token') {
    apiAuth.logout();
    router.navigate(['/']);
    return throwError(() => erro);
  }

  if (!apiAuth.tokenRefreshLogado()) {
    router.navigate(['/']);
    return throwError(() => erro);
  }

  // Se o instituto estiver bloqueado, loginTokenRefresh (via addSession) lança
  // AcessoObjetivoBloqueadoError de forma síncrona dentro do pipe: o erro propaga direto por
  // este Observable (sem passar pelo switchMap) - o AcessoObjetivoService já navegou para
  // /acesso-objetivo (replaceUrl); aqui apenas deixamos o erro seguir, sem navegar para o
  // login e sem repetir a requisição original (next(requisicao) nunca é chamado).
  return apiAuth.loginTokenRefresh().pipe(
    switchMap(x => {
      if (!x.valida) {
        apiAuth.logout();
        router.navigate(['/']);
        return throwError(() => erro);
      }
      // A requisicao original ainda carrega o header Authorization com o token vencido
      // (montado antes do 401). Precisa reclonar com o token novo devolvido pelo refresh,
      // senão a repetição cai de novo em 401 - este retry não passa de novo por este
      // interceptor (next() segue para o restante da cadeia), então não é recursivo.
      const requisicaoComTokenNovo = requisicao.clone({
        headers: requisicao.headers.set('Authorization', 'Basic ' + btoa(`${environment.AutenticacaoApi_HeaderUser}:${x.token}`))
      });
      return next(requisicaoComTokenNovo);
    })
  );
}

function ofMockedIpify(): Observable<HttpEvent<unknown>> {
  return of(new HttpResponse({ status: 200, body: { ip: '0.0.0.0' } }) as HttpEvent<unknown>);
}

function tratarErroGenerico(erro: HttpErrorResponse): Observable<never> {

  let mensagem = 'Desculpe, não foi possível atender sua solicitação. Por favor, tente novamente.';
  let titulo = 'Desculpe ocorreu um erro';

  if (erro.status >= 400 && erro.status < 500) {
    titulo = '';
    const corpo = erro.error as RetornoErroApi;
    if (!corpo?.mensagens) {
      mensagem = `${corpo?.mensagem ?? mensagem} (código: ${corpo?.idLog ?? 'desconhecido'})`;
    } else {
      mensagem = `${corpo.mensagens[0]}`;
    }
  }

  if (erro.status >= 500) {
    titulo = (erro.error as RetornoErroApi)?.errorMessage ?? titulo;
    mensagem = '';
  }

  Swal.fire(titulo, mensagem, 'error');
  return throwError(() => ({ message: mensagem }));
}
